import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';

import { mapStaffImportRowsToGridRows } from '../helpers/staffImportGridMapping';
import { getStaffMultiEditorPageUrl } from '../helpers/loginUrl';
import {
    createImportJob,
    fetchImportJobData,
    getLatestGeneratedImportJob,
    pollImportJobStatus,
    processImport,
    startWebCrawl as startWebCrawlRequest,
    uploadImportJobArtifacts,
} from '../services/staffImportService';
import { WEBSITE_IMPORT_LABEL } from '../constants/staffImport';
import type {
    GeneratedImportJobSummary,
    StaffImportAction,
    StaffImportGridRow,
    StaffImportSource,
    StaffImportState,
    StaffImportToast,
} from '../types/staffImport';
import {
    deriveGridReadyForImport,
    deriveHasExistingGridImport,
    deriveHasUnsavedImportProgress,
    isFullImportSuccess,
} from '../types/staffImport';

export const initialStaffImportState: StaffImportState = {
    step: 'form',
    selectedFile: null,
    uploadedFileName: null,
    importJobId: null,
    importSource: null,
    isUserInitiated: false,
    uploading: false,
    polling: false,
    pollTimedOut: false,
    processing: false,
    isDirty: false,
    csvInlineError: null,
    webCrawlInlineError: null,
    toast: null,
    analyzeResult: null,
    completedAt: null,
    gridRows: [],
    processResult: null,
    showConfirmModal: false,
    isReadyForImport: false,
    staffPageUrl: null,
};

const inlineErrorsForSource = (
    importSource: StaffImportSource | null | undefined,
    message: string,
): Pick<StaffImportState, 'csvInlineError' | 'webCrawlInlineError'> =>
    importSource === 'website'
        ? { csvInlineError: null, webCrawlInlineError: message }
        : { csvInlineError: message, webCrawlInlineError: null };

export const staffImportReducer = (
    state: StaffImportState,
    action: StaffImportAction,
): StaffImportState => {
    switch (action.type) {
        case 'SET_SELECTED_FILE':
            return {
                ...state,
                selectedFile: action.file,
                csvInlineError: null,
                uploading: false,
                polling: false,
                pollTimedOut: false,
                analyzeResult: null,
                completedAt: null,
                gridRows: [],
                uploadedFileName: null,
                importJobId: null,
                isReadyForImport: false,
                importSource: null,
                isUserInitiated: false,
                staffPageUrl: null,
            };
        case 'DISMISS_TOAST':
            return {
                ...state,
                toast: null,
            };
        case 'UPLOAD_START':
            return {
                ...state,
                uploading: true,
                polling: false,
                pollTimedOut: false,
                csvInlineError: null,
                webCrawlInlineError: null,
                toast: null,
                importSource: action.importSource,
                isUserInitiated: action.isUserInitiated,
                selectedFile:
                    action.importSource === 'website'
                        ? null
                        : state.selectedFile,
                uploadedFileName: null,
                importJobId: null,
                analyzeResult: null,
                completedAt: null,
                gridRows: [],
                isReadyForImport: false,
                staffPageUrl: null,
            };
        case 'UPLOAD_SUCCESS':
            return {
                ...state,
                uploading: false,
                uploadedFileName: action.uploadedFileName,
                importJobId: action.importJobId,
                importSource: action.importSource,
                isUserInitiated: action.isUserInitiated,
                staffPageUrl: action.staffPageUrl ?? null,
            };
        case 'UPLOAD_FAILURE':
            return {
                ...state,
                uploading: false,
                polling: false,
                ...inlineErrorsForSource(
                    action.importSource,
                    action.inlineError,
                ),
                toast: action.toast,
            };
        case 'POLL_START':
            return {
                ...state,
                polling: true,
                pollTimedOut: false,
            };
        case 'POLL_TIMEOUT':
            return {
                ...state,
                polling: false,
                pollTimedOut: true,
                ...inlineErrorsForSource(
                    state.importSource,
                    action.inlineError,
                ),
                toast: action.toast,
            };
        case 'POLL_FAILURE':
            return {
                ...state,
                uploading: false,
                polling: false,
                pollTimedOut: false,
                ...inlineErrorsForSource(
                    state.importSource,
                    action.inlineError,
                ),
                toast: action.toast,
            };
        case 'ANALYZE_SUCCESS': {
            const gridRows = mapStaffImportRowsToGridRows(action.result.rows);
            const isReadyForImport =
                action.result.validation.issues.length === 0 &&
                deriveGridReadyForImport(
                    gridRows,
                    action.result.existingBusinessEmails,
                );
            return {
                ...state,
                uploading: false,
                polling: false,
                analyzeResult: action.result,
                completedAt: action.completedAt,
                gridRows,
                importJobId: action.result.jobId,
                isReadyForImport,
                isDirty: false,
                step: 'form',
            };
        }
        case 'ANALYZE_FAILURE':
            return {
                ...state,
                uploading: false,
                polling: false,
                selectedFile: null,
                uploadedFileName: null,
                importJobId: null,
                importSource: null,
                isUserInitiated: false,
                completedAt: null,
                staffPageUrl: null,
                ...inlineErrorsForSource(
                    action.importSource,
                    action.inlineError,
                ),
                toast: action.toast,
            };
        case 'CLEAR_WEB_CRAWL_INLINE_ERROR':
            return {
                ...state,
                webCrawlInlineError: null,
            };
        case 'ADOPT_DISCARD':
            return {
                ...state,
                polling: false,
                uploadedFileName: null,
                importJobId: null,
                importSource: null,
                isUserInitiated: false,
                staffPageUrl: null,
            };
        case 'SET_GRID_ROWS': {
            const previousRowsById = new Map(
                state.gridRows.map((row) => [row.id, row]),
            );
            const nextRowsById = new Map(
                action.gridRows.map((row) => [row.id, row]),
            );
            const remainingIssues =
                state.analyzeResult?.validation.issues.filter((issue) => {
                    const rowId = `row-${issue.rowNumber - 1}`;
                    const previous = previousRowsById.get(rowId);
                    const next = nextRowsById.get(rowId);
                    const field = issue.field as keyof StaffImportGridRow;
                    return (
                        previous != null &&
                        next != null &&
                        previous[field] === next[field]
                    );
                }) || [];
            return {
                ...state,
                gridRows: action.gridRows,
                isReadyForImport:
                    remainingIssues.length === 0 &&
                    deriveGridReadyForImport(
                        action.gridRows,
                        state.analyzeResult?.existingBusinessEmails ?? [],
                    ),
                isDirty: true,
                analyzeResult: state.analyzeResult
                    ? {
                          ...state.analyzeResult,
                          validation: {
                              ...state.analyzeResult.validation,
                              issues: remainingIssues,
                          },
                      }
                    : null,
            };
        }
        case 'RETRY_FAILED_ROWS':
            return {
                ...state,
                step: 'form',
                processResult: null,
                gridRows: action.gridRows,
                isReadyForImport: deriveGridReadyForImport(
                    action.gridRows,
                    state.analyzeResult?.existingBusinessEmails ?? [],
                ),
                isDirty: true,
            };
        case 'GO_TO_PREVIEW':
            return {
                ...state,
                step: 'preview',
            };
        case 'GO_TO_ANALYZE':
            return {
                ...state,
                step: 'analyze',
            };
        case 'GO_TO_FORM':
            return {
                ...state,
                step: 'form',
            };
        case 'SHOW_CONFIRM_MODAL':
            return {
                ...state,
                showConfirmModal: true,
            };
        case 'HIDE_CONFIRM_MODAL':
            return {
                ...state,
                showConfirmModal: false,
            };
        case 'PROCESS_START':
            return {
                ...state,
                processing: true,
                toast: null,
            };
        case 'PROCESS_SUCCESS':
            return {
                ...state,
                processing: false,
                showConfirmModal: false,
                processResult: action.result,
                step: 'complete',
            };
        case 'PROCESS_FAILURE':
            return {
                ...state,
                processing: false,
                showConfirmModal: false,
                toast: action.toast,
            };
        case 'RESET':
            return {
                ...initialStaffImportState,
            };
        default:
            return state;
    }
};

const buildErrorToast = (message: string): StaffImportToast => ({
    variant: 'error',
    title: 'Error fetching data',
    message,
});

const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const INITIAL_POLL_INTERVAL_MS = 2000;
const MAX_POLL_INTERVAL_MS = 5000;
const INITIAL_LATEST_JOB_POLL_INTERVAL_MS = 5000;
const MAX_LATEST_JOB_POLL_INTERVAL_MS = 10000;

type PollJobOptions = {
    skipEmptyRows?: boolean;
    suppressErrors?: boolean;
    importSource?: StaffImportSource;
};

const sessionKey = (businessName: string) => `staff-import-job:${businessName}`;

const STAFF_IMPORT_TYPE = 'staff';

type PersistedImportJob = {
    jobId: string;
    fileName: string;
    importType: string;
    importSource?: StaffImportSource;
};

const isValidPersistedImportJob = (
    value: unknown,
): value is PersistedImportJob => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const job = value as PersistedImportJob;
    if (
        typeof job.jobId !== 'string' ||
        job.jobId.length === 0 ||
        typeof job.fileName !== 'string' ||
        job.fileName.length === 0 ||
        job.importType !== STAFF_IMPORT_TYPE
    ) {
        return false;
    }

    return (
        job.importSource === undefined ||
        job.importSource === 'csv' ||
        job.importSource === 'website'
    );
};

const readPersistedImportJob = (
    businessName: string,
): PersistedImportJob | null => {
    if (!businessName) {
        return null;
    }

    const persisted = sessionStorage.getItem(sessionKey(businessName));
    if (!persisted) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(persisted);
        if (!isValidPersistedImportJob(parsed)) {
            sessionStorage.removeItem(sessionKey(businessName));
            return null;
        }
        return parsed;
    } catch {
        sessionStorage.removeItem(sessionKey(businessName));
        return null;
    }
};

export const createInitialStaffImportState = (
    businessName: string,
): StaffImportState => {
    const job = readPersistedImportJob(businessName);
    if (!job) {
        return initialStaffImportState;
    }

    return {
        ...initialStaffImportState,
        uploadedFileName: job.fileName,
        importJobId: job.jobId,
        importSource: job.importSource ?? null,
        isUserInitiated: false,
        polling: true,
    };
};

const isRetryableApiError = (error: unknown) =>
    error instanceof TypeError ||
    (error instanceof Error &&
        (error as Error & { retryable?: boolean }).retryable === true);

const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === 'AbortError';

const resolveWebsiteImportFileName = (job: GeneratedImportJobSummary) =>
    job.trigger?.sourceUrl?.trim() || WEBSITE_IMPORT_LABEL;

const persistImportJob = (
    businessName: string,
    jobId: string,
    fileName: string,
    importSource: StaffImportSource,
) => {
    sessionStorage.setItem(
        sessionKey(businessName),
        JSON.stringify({
            jobId,
            fileName,
            importType: STAFF_IMPORT_TYPE,
            importSource,
        }),
    );
};

const clearPersistedImportJob = (businessName: string) => {
    sessionStorage.removeItem(sessionKey(businessName));
};

export const useStaffImportFlow = (businessName: string) => {
    const [state, dispatch] = useReducer(
        staffImportReducer,
        businessName,
        createInitialStaffImportState,
    );
    const [showAbandonModal, setShowAbandonModal] = useState(false);
    const [pendingLeaveUrl, setPendingLeaveUrl] = useState<string | null>(null);
    const operationIdRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const latestJobPollAbortRef = useRef<AbortController | null>(null);
    const latestJobPollTimeoutRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const latestJobPollInFlightRef = useRef(false);
    const handledAutomaticJobIdsRef = useRef<Set<string>>(new Set());
    const lastSeenAutomaticJobIdRef = useRef<string | null>(null);
    const importTypeRef = useRef<string>(STAFF_IMPORT_TYPE);
    const hasUnsavedImportProgressRef = useRef(
        deriveHasUnsavedImportProgress(state),
    );

    const hasUnsavedImportProgress = useMemo(
        () => deriveHasUnsavedImportProgress(state),
        [state],
    );

    const abandonCurrentOperation = useCallback(() => {
        operationIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    }, []);

    const stopLatestJobPolling = useCallback(() => {
        latestJobPollAbortRef.current?.abort();
        latestJobPollAbortRef.current = null;
        if (latestJobPollTimeoutRef.current) {
            clearTimeout(latestJobPollTimeoutRef.current);
            latestJobPollTimeoutRef.current = null;
        }
        latestJobPollInFlightRef.current = false;
    }, []);

    const pollJob = useCallback(
        async (
            jobId: string,
            fileName: string,
            importType: string,
            options?: PollJobOptions,
        ) => {
            abandonCurrentOperation();
            importTypeRef.current = importType;
            const operationId = operationIdRef.current;
            const controller = new AbortController();
            abortControllerRef.current = controller;
            dispatch({ type: 'POLL_START' });

            const deadline = Date.now() + POLL_TIMEOUT_MS;
            let interval = INITIAL_POLL_INTERVAL_MS;
            try {
                while (Date.now() < deadline) {
                    let status;
                    try {
                        status = await pollImportJobStatus(jobId, {
                            businessName,
                            fileName,
                            importType,
                            signal: controller.signal,
                        });
                    } catch (error) {
                        if (!isRetryableApiError(error)) {
                            throw error;
                        }
                        await new Promise((resolve) => {
                            setTimeout(resolve, interval);
                        });
                        if (operationId !== operationIdRef.current) {
                            return;
                        }
                        interval = Math.min(
                            interval + 500,
                            MAX_POLL_INTERVAL_MS,
                        );
                        continue;
                    }
                    if (operationId !== operationIdRef.current) {
                        return;
                    }
                    if (status.status === 'failed') {
                        sessionStorage.removeItem(sessionKey(businessName));
                        throw new Error(
                            status.error || 'Import processing failed.',
                        );
                    }
                    if (status.status === 'ready_for_review') {
                        let result;
                        try {
                            result = await fetchImportJobData(jobId, {
                                businessName,
                                fileName,
                                importType,
                                signal: controller.signal,
                            });
                        } catch (error) {
                            if (isRetryableApiError(error)) {
                                await new Promise((resolve) => {
                                    setTimeout(resolve, interval);
                                });
                                if (operationId !== operationIdRef.current) {
                                    return;
                                }
                                interval = Math.min(
                                    interval + 500,
                                    MAX_POLL_INTERVAL_MS,
                                );
                                continue;
                            }
                            throw error;
                        }
                        if (operationId !== operationIdRef.current) {
                            return;
                        }
                        if (
                            options?.skipEmptyRows &&
                            result.rows.length === 0
                        ) {
                            sessionStorage.removeItem(sessionKey(businessName));
                            dispatch({ type: 'ADOPT_DISCARD' });
                            return;
                        }
                        dispatch({
                            type: 'ANALYZE_SUCCESS',
                            result,
                            completedAt: status.updatedAt,
                        });
                        return;
                    }
                    if (
                        status.status !== 'uploaded' &&
                        status.status !== 'processing'
                    ) {
                        throw new Error(
                            `Unsupported import job status: ${status.status}`,
                        );
                    }
                    await new Promise((resolve) => {
                        setTimeout(resolve, interval);
                    });
                    if (operationId !== operationIdRef.current) {
                        return;
                    }
                    interval = Math.min(interval + 500, MAX_POLL_INTERVAL_MS);
                }

                const timeoutMessage =
                    'Processing is taking longer than expected. Retry to continue checking this job.';
                if (operationId !== operationIdRef.current) {
                    return;
                }
                dispatch({
                    type: 'POLL_TIMEOUT',
                    inlineError: timeoutMessage,
                    toast: buildErrorToast(timeoutMessage),
                });
            } catch (error) {
                if (
                    controller.signal.aborted ||
                    operationId !== operationIdRef.current
                ) {
                    return;
                }
                if (options?.suppressErrors) {
                    sessionStorage.removeItem(sessionKey(businessName));
                    dispatch({ type: 'ADOPT_DISCARD' });
                    return;
                }
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Unable to process this CSV.';
                clearPersistedImportJob(businessName);
                dispatch({
                    type: 'ANALYZE_FAILURE',
                    inlineError: errorMessage,
                    importSource: options?.importSource ?? 'csv',
                    toast: buildErrorToast(errorMessage),
                });
            }
        },
        [abandonCurrentOperation, businessName],
    );

    const adoptGeneratedImportJob = useCallback(
        async (job: GeneratedImportJobSummary) => {
            if (handledAutomaticJobIdsRef.current.has(job.jobId)) {
                return;
            }
            if (hasUnsavedImportProgressRef.current) {
                lastSeenAutomaticJobIdRef.current = job.jobId;
                return;
            }
            if (readPersistedImportJob(businessName)) {
                return;
            }
            if (job.status === 'failed') {
                handledAutomaticJobIdsRef.current.add(job.jobId);
                return;
            }
            if (job.status !== 'ready_for_review') {
                return;
            }
            if (job.rowCount === 0) {
                handledAutomaticJobIdsRef.current.add(job.jobId);
                return;
            }

            const fileName = resolveWebsiteImportFileName(job);
            if (
                hasUnsavedImportProgressRef.current ||
                readPersistedImportJob(businessName)
            ) {
                lastSeenAutomaticJobIdRef.current = job.jobId;
                return;
            }
            handledAutomaticJobIdsRef.current.add(job.jobId);
            lastSeenAutomaticJobIdRef.current = null;
            persistImportJob(businessName, job.jobId, fileName, 'website');
            dispatch({
                type: 'UPLOAD_SUCCESS',
                uploadedFileName: fileName,
                importJobId: job.jobId,
                importSource: 'website',
                isUserInitiated: false,
                staffPageUrl: job.staffPageUrl?.trim() || null,
            });
            await pollJob(job.jobId, fileName, STAFF_IMPORT_TYPE, {
                skipEmptyRows: true,
                suppressErrors: true,
                importSource: 'website',
            });
        },
        [businessName, pollJob],
    );

    useEffect(() => {
        const job = readPersistedImportJob(businessName);
        if (!job) {
            return;
        }

        void pollJob(job.jobId, job.fileName, job.importType, {
            importSource: job.importSource ?? 'csv',
        });
    }, [abandonCurrentOperation, businessName, pollJob]);

    useEffect(() => {
        if (!businessName) {
            return undefined;
        }
        if (readPersistedImportJob(businessName)) {
            return undefined;
        }

        const abortController = new AbortController();
        latestJobPollAbortRef.current = abortController;
        let interval = INITIAL_LATEST_JOB_POLL_INTERVAL_MS;

        const scheduleNextPoll = () => {
            if (abortController.signal.aborted) {
                return;
            }
            latestJobPollTimeoutRef.current = setTimeout(() => {
                void pollLatest();
            }, interval);
            interval = Math.min(
                interval + 1000,
                MAX_LATEST_JOB_POLL_INTERVAL_MS,
            );
        };

        const pollLatest = async () => {
            if (
                abortController.signal.aborted ||
                latestJobPollInFlightRef.current
            ) {
                return;
            }
            if (hasUnsavedImportProgressRef.current) {
                scheduleNextPoll();
                return;
            }
            if (readPersistedImportJob(businessName)) {
                return;
            }

            latestJobPollInFlightRef.current = true;
            try {
                const { job } = await getLatestGeneratedImportJob({
                    businessName,
                    signal: abortController.signal,
                });
                if (abortController.signal.aborted) {
                    return;
                }
                if (job) {
                    if (
                        job.jobId === lastSeenAutomaticJobIdRef.current &&
                        hasUnsavedImportProgressRef.current
                    ) {
                        return;
                    }
                    await adoptGeneratedImportJob(job);
                }
            } catch (error) {
                if (!isAbortError(error)) {
                    // Background lookup failures must not disturb the UI.
                }
            } finally {
                latestJobPollInFlightRef.current = false;
                if (!abortController.signal.aborted) {
                    scheduleNextPoll();
                }
            }
        };

        void pollLatest();

        return () => {
            abortController.abort();
            if (latestJobPollTimeoutRef.current) {
                clearTimeout(latestJobPollTimeoutRef.current);
                latestJobPollTimeoutRef.current = null;
            }
            latestJobPollInFlightRef.current = false;
            if (latestJobPollAbortRef.current === abortController) {
                latestJobPollAbortRef.current = null;
            }
        };
    }, [adoptGeneratedImportJob, businessName]);

    useEffect(
        () => () => {
            abandonCurrentOperation();
            stopLatestJobPolling();
        },
        [abandonCurrentOperation, stopLatestJobPolling],
    );

    useEffect(() => {
        hasUnsavedImportProgressRef.current = hasUnsavedImportProgress;
    }, [hasUnsavedImportProgress]);

    useEffect(() => {
        const warnBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasUnsavedImportProgressRef.current) {
                return;
            }
            event.preventDefault();
            event.returnValue = '';
        };

        const clearPersistedJobOnLeave = (event: PageTransitionEvent) => {
            if (hasUnsavedImportProgressRef.current && !event.persisted) {
                sessionStorage.removeItem(sessionKey(businessName));
            }
        };

        window.addEventListener('beforeunload', warnBeforeUnload);
        window.addEventListener('pagehide', clearPersistedJobOnLeave);

        return () => {
            window.removeEventListener('beforeunload', warnBeforeUnload);
            window.removeEventListener('pagehide', clearPersistedJobOnLeave);
        };
    }, [businessName]);

    const setSelectedFile = useCallback(
        (file: File | null) => {
            abandonCurrentOperation();
            clearPersistedImportJob(businessName);
            dispatch({ type: 'SET_SELECTED_FILE', file });
        },
        [abandonCurrentOperation, businessName],
    );

    const dismissToast = useCallback(() => {
        dispatch({ type: 'DISMISS_TOAST' });
    }, []);

    const clearWebCrawlInlineError = useCallback(() => {
        dispatch({ type: 'CLEAR_WEB_CRAWL_INLINE_ERROR' });
    }, []);

    const uploadSelectedCsv = useCallback(async () => {
        if (!state.selectedFile) {
            return;
        }

        const file = state.selectedFile;
        abandonCurrentOperation();
        const uploadOperationId = operationIdRef.current;
        const uploadController = new AbortController();
        abortControllerRef.current = uploadController;
        clearPersistedImportJob(businessName);
        dispatch({
            type: 'UPLOAD_START',
            importSource: 'csv',
            isUserInitiated: true,
        });

        try {
            const job = await createImportJob(file.name, { businessName });
            if (uploadOperationId !== operationIdRef.current) {
                return;
            }
            await uploadImportJobArtifacts(job, file, uploadController.signal);
            if (uploadOperationId !== operationIdRef.current) {
                return;
            }

            dispatch({
                type: 'UPLOAD_SUCCESS',
                uploadedFileName: file.name,
                importJobId: job.jobId,
                importSource: 'csv',
                isUserInitiated: true,
            });
            persistImportJob(businessName, job.jobId, file.name, 'csv');
            await pollJob(job.jobId, file.name, STAFF_IMPORT_TYPE, {
                importSource: 'csv',
            });
        } catch (error) {
            if (uploadOperationId !== operationIdRef.current) {
                return;
            }
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Unable to upload and process this CSV.';
            clearPersistedImportJob(businessName);
            dispatch({
                type: 'ANALYZE_FAILURE',
                inlineError: errorMessage,
                importSource: 'csv',
                toast: buildErrorToast(errorMessage),
            });
        }
    }, [abandonCurrentOperation, businessName, pollJob, state.selectedFile]);

    const startWebCrawl = useCallback(
        async (url: string) => {
            const trimmedUrl = url.trim();
            if (!trimmedUrl) {
                return;
            }

            abandonCurrentOperation();
            const crawlOperationId = operationIdRef.current;
            clearPersistedImportJob(businessName);
            dispatch({
                type: 'UPLOAD_START',
                importSource: 'website',
                isUserInitiated: true,
            });

            try {
                const job = await startWebCrawlRequest(trimmedUrl, {
                    businessName,
                });
                if (crawlOperationId !== operationIdRef.current) {
                    return;
                }

                dispatch({
                    type: 'UPLOAD_SUCCESS',
                    uploadedFileName: trimmedUrl,
                    importJobId: job.jobId,
                    importSource: 'website',
                    isUserInitiated: true,
                });
                persistImportJob(
                    businessName,
                    job.jobId,
                    trimmedUrl,
                    'website',
                );
                await pollJob(job.jobId, trimmedUrl, STAFF_IMPORT_TYPE, {
                    importSource: 'website',
                });
            } catch (error) {
                if (crawlOperationId !== operationIdRef.current) {
                    return;
                }
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Unable to load staff from this website.';
                clearPersistedImportJob(businessName);
                dispatch({
                    type: 'ANALYZE_FAILURE',
                    inlineError: errorMessage,
                    importSource: 'website',
                    toast: buildErrorToast(errorMessage),
                });
            }
        },
        [abandonCurrentOperation, businessName, pollJob],
    );

    const retryPolling = useCallback(() => {
        if (state.importJobId && state.uploadedFileName) {
            void pollJob(
                state.importJobId,
                state.uploadedFileName,
                importTypeRef.current,
                {
                    importSource: state.importSource ?? 'csv',
                },
            );
        }
    }, [pollJob, state.importJobId, state.uploadedFileName]);

    const goToPreview = useCallback(() => {
        dispatch({ type: 'GO_TO_PREVIEW' });
    }, []);

    const goToAnalyze = useCallback(() => {
        dispatch({ type: 'GO_TO_ANALYZE' });
    }, []);

    const goToForm = useCallback(() => {
        dispatch({ type: 'GO_TO_FORM' });
    }, []);

    const showConfirmModal = useCallback(() => {
        dispatch({ type: 'SHOW_CONFIRM_MODAL' });
    }, []);

    const hideConfirmModal = useCallback(() => {
        dispatch({ type: 'HIDE_CONFIRM_MODAL' });
    }, []);

    const setGridRows = useCallback((gridRows: StaffImportGridRow[]) => {
        dispatch({ type: 'SET_GRID_ROWS', gridRows });
    }, []);

    const processStaffImport = useCallback(async () => {
        if (!state.uploadedFileName || state.gridRows.length === 0) {
            return;
        }
        if (!state.importJobId) {
            dispatch({
                type: 'PROCESS_FAILURE',
                toast: buildErrorToast(
                    'Import job id is missing. Re-run the import before processing.',
                ),
            });
            return;
        }

        dispatch({ type: 'PROCESS_START' });
        hasUnsavedImportProgressRef.current = false;

        try {
            const result = await processImport(
                state.uploadedFileName,
                state.gridRows,
                {
                    businessName,
                    jobId: state.importJobId,
                },
            );

            sessionStorage.removeItem(sessionKey(businessName));

            if (isFullImportSuccess(result)) {
                hasUnsavedImportProgressRef.current = false;
                window.location.assign(
                    getStaffMultiEditorPageUrl(
                        businessName,
                        state.importJobId,
                        result.importedCount,
                    ),
                );
                return;
            }

            dispatch({
                type: 'PROCESS_SUCCESS',
                result,
            });
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Unable to process this import.';
            dispatch({
                type: 'PROCESS_FAILURE',
                toast: buildErrorToast(errorMessage),
            });
        }
    }, [
        businessName,
        state.gridRows,
        state.importJobId,
        state.uploadedFileName,
    ]);

    const retryFailedRows = useCallback(() => {
        if (!state.processResult) {
            return;
        }
        const failedIds = new Set(
            state.processResult.results
                .filter((result) => result.status === 'failed')
                .map((result) => result.rowId),
        );
        dispatch({
            type: 'RETRY_FAILED_ROWS',
            gridRows: state.gridRows.filter((row) => failedIds.has(row.id)),
        });
    }, [state.gridRows, state.processResult]);

    const reset = useCallback(() => {
        const currentJobId = state.importJobId;
        if (currentJobId) {
            handledAutomaticJobIdsRef.current.add(currentJobId);
        }
        abandonCurrentOperation();
        sessionStorage.removeItem(sessionKey(businessName));
        dispatch({ type: 'RESET' });
        hasUnsavedImportProgressRef.current = false;
    }, [abandonCurrentOperation, businessName, state.importJobId]);

    const cancelImport = useCallback(() => {
        if (hasUnsavedImportProgress) {
            setPendingLeaveUrl(null);
            setShowAbandonModal(true);
            return;
        }

        reset();
    }, [hasUnsavedImportProgress, reset]);

    const requestLeave = useCallback(
        (url: string) => {
            if (hasUnsavedImportProgress) {
                setPendingLeaveUrl(url);
                setShowAbandonModal(true);
                return;
            }

            window.location.assign(url);
        },
        [hasUnsavedImportProgress],
    );

    const cancelLeave = useCallback(() => {
        setShowAbandonModal(false);
        setPendingLeaveUrl(null);
    }, []);

    const confirmLeave = useCallback(() => {
        const leaveUrl = pendingLeaveUrl;
        setShowAbandonModal(false);
        setPendingLeaveUrl(null);
        reset();

        if (leaveUrl) {
            window.location.assign(leaveUrl);
        }
    }, [pendingLeaveUrl, reset]);

    const hasGridPreview = Boolean(state.analyzeResult);
    const hasExistingGridImport = deriveHasExistingGridImport(state);

    const importBusy = state.uploading || state.polling;
    const controlsDisabled = importBusy || state.processing;
    const csvUploadedFileName =
        state.importSource === 'csv' ? state.uploadedFileName : null;
    const csvUploadLoading =
        state.importSource === 'csv' && state.isUserInitiated && importBusy;
    const csvDisabled = controlsDisabled;
    const webCrawlLoading =
        state.importSource === 'website' && state.isUserInitiated && importBusy;
    const webCrawlDisabled = controlsDisabled;
    const statusProcessing = importBusy;

    return {
        state,
        setSelectedFile,
        setGridRows,
        dismissToast,
        clearWebCrawlInlineError,
        uploadSelectedCsv,
        startWebCrawl,
        retryPolling,
        goToPreview,
        goToAnalyze,
        goToForm,
        showConfirmModal,
        hideConfirmModal,
        processStaffImport,
        retryFailedRows,
        reset,
        cancelImport,
        hasGridPreview,
        hasExistingGridImport,
        hasUnsavedImportProgress,
        showAbandonModal,
        pendingLeaveUrl,
        requestLeave,
        cancelLeave,
        confirmLeave,
        csvUploadedFileName,
        csvUploadLoading,
        csvDisabled,
        webCrawlLoading,
        webCrawlDisabled,
        statusProcessing,
    };
};
